import { useCallback, useEffect, useState } from 'react';
import { useFlash } from 'contexts/flash';
import axios from 'axios';
import { getUsers } from 'services/option';

export type TDailyReportSchedule = {
  time: string;
  enabled: boolean;
};

export type TWeeklyReportSchedule = {
  time: string;
  enabled: boolean;
  dayOfWeek: string;
};

export type TReminderFrequency = {
  unit: 'HOURS' | 'DAYS';
  value: number;
};

export type TNotificationSettings = {
  defaultNotificationWindow: number;
  emailRecipients: string[];
  dailyReportSchedule: TDailyReportSchedule;
  weeklyReportSchedule: TWeeklyReportSchedule;
  reminderFrequency: TReminderFrequency;
};

export const DAYS_OF_WEEK = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' }
];

export const REMINDER_UNITS = [
  { value: 'HOURS', label: 'Hours' },
  { value: 'DAYS', label: 'Days' }
];

export const DEFAULT_SETTINGS: TNotificationSettings = {
  defaultNotificationWindow: 24,
  emailRecipients: [],
  dailyReportSchedule: { time: '08:00', enabled: true },
  weeklyReportSchedule: { time: '09:00', enabled: true, dayOfWeek: 'MONDAY' },
  reminderFrequency: { unit: 'HOURS', value: 24 }
};

export type TUserOption = {
  email: string;
  name: string;
};

export const useInjection = () => {
  const [settings, setSettings] = useState<TNotificationSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  const [userOptions, setUserOptions] = useState<TUserOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { setFlash } = useFlash();

  const handleGetSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`/notification-settings`);
      setSettings(data.notificationSettings ?? DEFAULT_SETTINGS); // ← fallback
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // If it's a 404 (no settings exist yet), silently use defaults
        if (error?.response?.status === 404) {
          setSettings(DEFAULT_SETTINGS);
        } else {
          setFlash({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to load notification settings'
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGetUsers = useCallback(async () => {
    try {
      const data = await getUsers({ page: 1, size: 99999 });
      const users: TUserOption[] = (data?.users ?? []).map((u: TUserOption) => ({
        email: u.email,
        name: u.name
      }));
      setUserOptions(users);
    } catch {
      // silently fail — dropdown just won't show suggestions
    }
  }, []);

  const handleSaveSettings = useCallback(async (payload: TNotificationSettings) => {
    setIsSaving(true);
    try {
      const { data } = await axios.put(`/notification-settings`, payload);
      setSettings(data.notificationSettings);
      setFlash({ type: 'success', message: 'Notification settings saved successfully!' });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setFlash({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to save notification settings'
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, []);

  useEffect(() => {
    handleGetSettings();
    handleGetUsers();
  }, []);

  return {
    settings,
    isLoading,
    isSaving,
    userOptions,
    handleSaveSettings
  };
};
